<template>
  <div class="admin-request-display">
    <!-- Problem Message -->
    <div class="alert alert-danger" v-if="reservation.completed && reservation.AdminRequests && reservation.AdminRequests[0] && !reservation.AdminRequests[0].actioned">
      <p><span class="fa fa-fw fa-exclamation-triangle"></span> <strong>Reservation had a problem.</strong></p>
      <hr>
      <p class="mb-1" v-for="problem in reservation.AdminRequests" :key="problem.uuid">
        <router-link v-if="!problem.actioned" class="btn btn-danger btn-sm" :to="{ path: '/follow-up/problems/' + problem.uuid }">
          View {{ problem.requestType }} Problem
        </router-link>
      </p>
    </div>
    <!-- Promotional Code -->
    <div class="alert alert-info" v-if="reservation.ReservationPromo">
      <p><span class="fa fa-fw fa-dollar-sign"></span> <strong>Promotional Code in use</strong></p>
      <p>{{ reservation.ReservationPromo.description }}</p>
    </div>
    <div class="list-group-item list-group-item-success d-flex align-items-center" v-if="reservation.FinanceCode">
      <h4 class="my-0">Finance Code Used</h4>
      <span class="ml-auto badge badge-success">{{ reservation.FinanceCode.code }} - {{ reservation.FinanceCode.description }}</span>
    </div>
    <div class="list-group-item" v-if="reservation.HeldChargeUuid">
      <p v-if="reservation.HeldCharge.captured || reservation.HeldCharge.refunded > 0" class="alert alert-success">
        Preauthorisation has been refunded / captured.
      </p>
      <p v-else class="alert alert-info">
        Preauthorisation available for capture.
      </p>
      <router-link class="btn btn-success btn-sm" :to="{ path: '/membership/members/billing/preauth/' + reservation.HeldChargeUuid }">
        <span class="fa fa-search"></span> View
      </router-link>
    </div>
    <!-- Breakdowns -->
    <div v-for="(breakdown, i) of reservation.breakdowns" :key="i">
      <price-table :key="i" v-if="priceTable(breakdown, reservation)"
        :container-class="breakdownType(breakdown.type, i).containerClass"
        :title="breakdownType(breakdown.type, i).heading"
        :icon="breakdownType(breakdown.type, i).icon"
        :price-table="priceTable(breakdown, reservation)">
      </price-table>
    </div>
    <!-- Trip Data -->
    <accordian-display v-if="reservation.completed && reservation.kmFee !== null && reservation.lateFee !== null"
      container-class="info"
      title="Reservation Completed"
      icon="fa-calendar-check"
      :default-open="true">
      <template slot="header">
        <tr>
          <th colspan="2">
            <span class="fa fa-hourglass-end fa-fw"></span>{{ reservation.completedEndDate | short_date_time_format(reservation.Lot.timezone) }}
          </th>
          <th colspan="2" :class="{ 'text-danger' : minutesLate }">
            {{ minutesLate | pretty_number }} minutes late
          </th>
        </tr>
        <tr>
          <th class="bg-primary text-white"><strong>Description</strong></th>
          <th class="bg-primary text-white"><strong>Units</strong></th>
          <th class="bg-primary text-white"><strong>Cost</strong></th>
          <th class="bg-primary text-white"><strong>Total</strong></th>
        </tr>
      </template>
      <tr class="text-danger" v-if="reservation.kmFee">
        <td>Driven KM</td>
        <td>
          <template v-if="unlimitedKms">
            {{ reservation.completedKms }} km<br>
            <span class="text-muted">(Unlimited kms included)</span>
          </template>
          <template v-else-if="includedKms">
            {{ reservation.completedKms }} km<br>
            <span class="text-muted">({{ includedKms }}km included)</span>
          </template>
          <template v-else>
            {{ reservation.completedKms }}
          </template>
        </td>
        <td>{{reservation.perKmCost | to_dollar_amount}}/km</td>
        <td>{{ reservation.kmFee | to_dollar_amount }}</td>
      </tr>
      <tr class="text-danger" v-if="reservation.lateFee">
        <td>Late Fee</td>
        <td>
          {{ latePeriod }} blocks of 15 minutes
        </td>
        <td>{{ reservation.hourlyRate | to_dollar_amount }}/15min block</td>
        <td>{{ reservation.lateFee | to_dollar_amount }}</td>
      </tr>
      <tr>
        <td colspan="3" class="font-weight-bold">Total</td>
        <td>
          <strong>{{ totalExtraFees | to_dollar_amount }}</strong>
        </td>
      </tr>
    </accordian-display>
    <accordian-display v-if="!reservation.FinanceCode && (reservation.CreditCharges.length || reservation.ReservationCharges.length || reservation.AccountCredits.length)"
      container-class="secondary"
      title="Payments"
      icon="fa-dollar-sign"
      :default-open="true">
      <template slot="header" class="thead-light">
        <tr>
          <th width="15%">
            <strong>Date</strong>
          </th>
          <th width="40%">
            <strong>Description</strong>
          </th>
          <th>
            <strong>Status</strong>
          </th>
          <th>
            <strong>Amount</strong>
          </th>
        </tr>
      </template>
      <template v-for="payment in reservation.CreditCharges">
        <template v-if="payment.description.indexOf(`Credit used for Reservation #${reservation.reservationId}`) !== -1">
          <tr :key="payment.uuid">
            <td>{{ payment.createdAt | short_date_format }}</td>
            <td>{{ payment.description }}</td>
            <td>
              <span class="badge badge-success">Paid</span>
            </td>
            <td>{{ payment.amount | to_dollar_amount }}</td>
          </tr>
        </template>
      </template>
      <template v-for="payment in reservation.ReservationCharges">
        <tr :key="payment.uuid">
          <td>{{ payment.createdAt | short_date_format }}</td>
          <td><a target="_blank" :href="'https://dashboard.stripe.com/payments/' + payment.chargeId">{{ payment.description }}</a></td>
          <td>
            <span class="badge badge-danger" v-if="payment.failed">Declined</span>
            <span class="badge badge-success" v-if="!payment.refunded && !payment.failed">Paid</span>
            <span class="badge badge-default" v-if="payment.refunded">Refunded</span>
          </td>
          <td>{{ payment.cost | to_dollar_amount }}</td>
        </tr>
      </template>
      <template v-for="payment in reservation.AccountCredits">
        <tr :key="payment.uuid">
          <td>{{ payment.createdAt | short_date_format }}</td>
          <td>Credit given back</td>
          <td>
            <span class="badge badge-info">Credited</span>
          </td>
          <td>{{ payment.amountUsed | to_dollar_amount }}</td>
        </tr>
      </template>
    </accordian-display>
  </div>
</template>

<script>
import Util from '@server/util'
import PriceTable from '@/components/displays/PriceTable'
import AccordianDisplay from '@/components/displays/AccordianDisplay'

export default {
  name: 'ReservationCosts',
  components: {
    PriceTable,
    AccordianDisplay
  },
  props: {
    reservation: {
      type: Object
    }
  },
  computed: {
    preAuthReleased () {
      if (this.reservation.HeldCharge) {
        return parseFloat(this.reservation.HeldCharge.refunded) === 0 &&
        !this.reservation.HeldCharge.captured &&
        !moment(this.reservation.HeldCharge.createdAt).add(7, 'days').isAfter(moment())
      } else {
        return true
      }
    },
    includedKms () {
      if (this.reservation.completed && this.reservation.breakdowns) {
        let breakdown = this.reservation.breakdowns[this.reservation.breakdowns.length - 1]
        let { priceBreakdown } = this.priceTable(breakdown, this.reservation)
        return priceBreakdown.days * this.reservation.includeKms + priceBreakdown.weekendDays * (this.reservation.includeKms * 2)
      }
      return false
    },
    unlimitedKms () {
      if (this.reservation.extras) {
        for (let extra of this.reservation.extras) {
          if (extra.unlimitedKms && extra.unlimitedKms.toString() === 'true') {
            return true
          }
        }
      }
      return false
    },
    minutesLate () {
      let sEnd = moment(this.reservation.endDate)
      let aEnd = moment(this.reservation.completedEndDate)
      let mDiff = aEnd.diff(sEnd, 'minutes')
      return mDiff
    },
    latePeriod () {
      let sEnd = moment(this.reservation.endDate)
      let aEnd = moment(this.reservation.completedEndDate)
      let mDiff = aEnd.diff(sEnd, 'minutes')
      if (mDiff > 5) {
        return Math.ceil(mDiff / 15.0)
      } else {
        return 0
      }
    },
    totalExtraFees () {
      let lateFee = parseFloat(this.reservation.lateFee)
      let kmFee = parseFloat(this.reservation.kmFee)
      return (lateFee + kmFee).toFixed(2)
    }
  },
  methods: {
    breakdownType (type, i) {
      switch (type) {
        case 'newReservation':
          if (i === 0) {
            return {
              heading: 'Creating Reservation',
              icon: 'fa-calendar-plus',
              containerClass: 'success'
            }
          } else {
            return {
              heading: 'Resending Reservation',
              icon: 'fa-cloud-upload-alt',
              containerClass: 'dark'
            }
          }
        case 'modifyReservation':
          return {
            heading: 'Modifying Reservation',
            icon: 'fa-pencil-alt',
            containerClass: 'amber'
          }
        case 'changingVehicles':
          return {
            heading: 'Changing Reservation',
            icon: 'fa-car',
            containerClass: 'amber'
          }
        case 'techPush':
          return {
            heading: 'Resending Reservation',
            icon: 'fa-cloud-upload-alt',
            containerClass: 'dark'
          }
      }
    },
    priceTable (breakdown, reservation) {
      if (breakdown) {
        let Rate = {
          dailyRate: reservation.dailyRate,
          hourlyRate: reservation.hourlyRate,
          weekendRate: reservation.weekendRate,
          overnightRate: reservation.overnightRate,
          includeKms: reservation.includeKms,
          perKmCost: reservation.perKmCost
        }
        return Util.calculateCosts({
          Organization: reservation.Car.Organization,
          Car: reservation.Car,
          Lot: reservation.Lot,
          Rate,
          startDate: moment(breakdown.startDate).format(),
          endDate: moment(breakdown.endDate).format(),
          extras: reservation.extras,
          useCredits: false,
          percentDiscount: reservation.percentDiscount
        })
      } else {
        return false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/ReservationCosts.vue